import React from 'react';
import PropTypes from 'prop-types';
import { getNavItemKey } from '../utils/navItemUtils';
import {
	classifyMegaMenuItem,
	shapeColumnItems,
	isColumnFullWidth,
	columnSkeletonHeight
} from '../utils/megaMenuColumnUtils';
import MegaMenuGroup from './MegaMenuGroup';
import MegaMenuLink from './MegaMenuLink';
import MegaMenuFragment from './MegaMenuFragment';

/**
 * Renders a single column within the mega menu. Shapes the column's raw items
 * (wrapping a flat-link-only column into one synthesized heading-group) and
 * dispatches each shaped item to the component for its kind.
 *
 * @param {object} props
 * @param {NavigationColumn} props.column
 * @param {string} [props.parentLabel] - Label of the featured item this column
 *   belongs to. Supplies the heading for any group (real or synthesized) that
 *   has no label of its own.
 * @param {() => void} props.onLinkClick
 */
const MegaMenuColumn = ({ column, parentLabel = undefined, onLinkClick }) => {
	const items = shapeColumnItems(column);
	const isSoleTopLevelItem = items.length === 1;
	const isFullWidth = isColumnFullWidth(items, column.gridUnits);
	const skeletonHeight = columnSkeletonHeight(column);

	return (
		<div className="ws-nav-drawer__mega-menu-column">
			{items.map((item, index) => {
				const key = getNavItemKey(item, index);
				switch (classifyMegaMenuItem(item)) {
					case 'fragment':
						return (
							<MegaMenuFragment
								key={key}
								url={item.url}
								skeletonHeight={skeletonHeight}
							/>
						);
					case 'group':
						return (
							<MegaMenuGroup
								key={key}
								item={item}
								parentLabel={parentLabel}
								onLinkClick={onLinkClick}
								isFullWidth={isFullWidth}
								standalone={isSoleTopLevelItem}
								skeletonHeight={skeletonHeight}
							/>
						);
					default:
						return (
							<MegaMenuLink
								key={key}
								item={item}
								onLinkClick={onLinkClick}
								standalone={isSoleTopLevelItem}
							/>
						);
				}
			})}
		</div>
	);
};

MegaMenuColumn.propTypes = {
	column: PropTypes.shape({
		gridUnits: PropTypes.number.isRequired,
		navigationItems: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.string,
				label: PropTypes.string,
				url: PropTypes.string,
				embedded: PropTypes.bool,
				navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
			})
		)
	}).isRequired,
	parentLabel: PropTypes.string,
	onLinkClick: PropTypes.func.isRequired
};

MegaMenuColumn.defaultProps = {
	parentLabel: undefined
};

export default MegaMenuColumn;
